import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const rp = (n) => "Rp" + (n == null ? "0" : Math.round(n).toLocaleString("id-ID"));
const SEV_TONE = { Critical: "bg-rose-500/15 text-rose-400 border-rose-500/30", Warning: "bg-amber-500/15 text-amber-400 border-amber-500/30", Info: "bg-slate-700/40 text-slate-300 border-slate-700" };
const CFG_FIELDS = [
  ["cash_min", "Saldo Kas/Bank Minimum (Rp)"],
  ["draft_journal_max", "Maks. Jurnal Draft"],
  ["waiting_hpp_max", "Maks. Transaksi Menunggu HPP"],
  ["unreconciled_max", "Maks. Sesi Rekonsiliasi Belum Selesai"],
  ["ap_outstanding_max", "Ambang Hutang Supplier (Rp, 0=nonaktif)"],
  ["ar_outstanding_max", "Ambang Piutang Belum Cair (Rp, 0=nonaktif)"],
  ["budget_overrun_pct", "Ambang Pelampauan Anggaran Beban (%)"],
];

export default function FinanceControl() {
  const { can } = useAuth();
  const canManage = can("finance.cashbank.manage");
  const isOwner = can("dashboard.owner");
  const [d, setD] = useState(null);
  const [owner, setOwner] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);

  const load = useCallback(async () => {
    try {
      setD((await api.get("/finance/control/dashboard")).data);
      if (isOwner) setOwner((await api.get("/finance/control/owner-summary")).data);
    } catch (e) { toast.error(apiError(e)); }
  }, [isOwner]);
  useEffect(() => { load(); }, [load]);

  const openCfg = async () => {
    try { setCfg((await api.get("/finance/control/alert-config")).data); setShowCfg(true); } catch (e) { toast.error(apiError(e)); }
  };
  const saveCfg = async () => {
    try {
      const body = {}; CFG_FIELDS.forEach(([k]) => { body[k] = Number(cfg[k] || 0); });
      await api.post("/finance/control/alert-config", body);
      toast.success("Threshold disimpan"); setShowCfg(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!d) return <div className="p-6 text-slate-400">Memuat…</div>;
  const m = d.metrics;

  return (
    <div className="p-6" data-testid="finance-control-page">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Kontrol Keuangan & Alert</h1>
          <p className="text-slate-400 text-sm">Ringkasan kontrol eksekutif · berbasis jurnal Posted · {d.as_of}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded ${d.critical_count ? "bg-rose-500/15 text-rose-400" : d.warning_count ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`} data-testid="alert-badge">
            {d.critical_count} kritis · {d.warning_count} peringatan
          </span>
          {canManage && <Button data-testid="open-config-btn" onClick={openCfg} className="bg-slate-700 hover:bg-slate-600 h-8 px-3 text-xs">Threshold</Button>}
        </div>
      </div>

      {isOwner && owner && (
        <Card className="p-4 bg-slate-900 border-slate-800 mb-4" data-testid="owner-control-summary">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-white font-medium">Owner Control Summary</h3>
            <span className={`text-sm px-3 py-1 rounded ${owner.health_score >= 80 ? "bg-emerald-500/15 text-emerald-400" : owner.health_score >= 50 ? "bg-amber-500/15 text-amber-400" : "bg-rose-500/15 text-rose-400"}`} data-testid="health-score">
              Skor Kontrol: {owner.health_score}/100
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Mini label="Kas & Bank" value={rp(owner.cash_bank)} />
            <Mini label="Omzet MTD" value={rp(owner.omzet_mtd)} tone="text-emerald-400" />
            <Mini label="Laba Bersih MTD" value={rp(owner.laba_bersih_mtd)} tone="text-emerald-400" />
            <Mini label="Piutang Belum Cair" value={rp(owner.ar_outstanding)} tone="text-amber-400" />
            <Mini label="Hutang Supplier" value={rp(owner.ap_outstanding)} tone="text-amber-400" />
            <Mini label="Jurnal Draft" value={owner.draft_pending} />
            <Mini label="Menunggu HPP" value={owner.orders_waiting_hpp} />
            <Mini label="Rekonsiliasi Terbuka" value={owner.open_recon} />
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Mini card label="Kas & Bank (GL)" value={rp(m.cash)} />
        <Mini card label="Piutang Outstanding" value={rp(m.ar.outstanding)} tone="text-amber-400" />
        <Mini card label="Hutang Outstanding" value={rp(m.ap.outstanding)} tone="text-amber-400" />
        <Mini card label="Jurnal Draft" value={`${m.draft.total_count}`} sub={rp(m.draft.total_value)} />
        <Mini card label="Menunggu HPP" value={`${m.hpp.orders}`} />
        <Mini card label="Rekonsiliasi Terbuka" value={`${m.open_recon}`} />
        <Mini card label="Periode Berjalan" value={d.current_period} sub={d.current_period_status} />
        <Mini card label="Laba Bersih MTD" value={rp(d.pnl_mtd.net_profit)} tone="text-emerald-400" />
      </div>

      {m.budget_adherence?.has_budget && (
        <Card className="p-4 bg-slate-900 border-slate-800 mb-4" data-testid="budget-adherence">
          <h3 className="text-white font-medium mb-1">Kepatuhan Anggaran Beban (YTD) · {m.budget_adherence.budget_name}</h3>
          <div className="flex gap-6 text-sm text-slate-300">
            <span>Anggaran: {rp(m.budget_adherence.expense_budget)}</span>
            <span>Aktual: {rp(m.budget_adherence.expense_actual)}</span>
            <span className={m.budget_adherence.variance_pct > 0 ? "text-rose-400" : "text-emerald-400"}>Selisih: {m.budget_adherence.variance_pct == null ? "—" : `${m.budget_adherence.variance_pct}%`}</span>
          </div>
        </Card>
      )}

      <Card className="p-4 bg-slate-900 border-slate-800" data-testid="alerts-list">
        <h3 className="text-white font-medium mb-2">Finance Alerts</h3>
        {d.alerts.length === 0 ? (
          <div className="text-emerald-400 text-sm" data-testid="no-alerts">✓ Tidak ada alert. Semua indikator dalam ambang aman.</div>
        ) : (
          <div className="space-y-2">
            {d.alerts.map((a) => (
              <div key={a.code} data-testid={`alert-${a.code}`} className={`flex justify-between items-center p-3 rounded border ${SEV_TONE[a.severity]}`}>
                <div><div className="text-sm font-medium">{a.label}</div><div className="text-xs opacity-80">{a.detail}</div></div>
                <span className="text-xs px-2 py-1 rounded bg-black/20">{a.severity}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {showCfg && cfg && (
        <Card className="p-4 bg-slate-900 border-slate-800 mt-4" data-testid="config-panel">
          <div className="flex justify-between mb-3"><h3 className="text-white font-medium">Konfigurasi Threshold Alert</h3>
            <button onClick={() => setShowCfg(false)} className="text-slate-400 text-sm">Tutup</button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CFG_FIELDS.map(([k, label]) => (
              <div key={k}><label className="text-xs text-slate-400">{label}</label>
                <Input data-testid={`cfg-${k}`} type="number" value={cfg[k] ?? 0} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            ))}
          </div>
          <div className="mt-3"><Button data-testid="save-config-btn" onClick={saveCfg} className="bg-emerald-600 hover:bg-emerald-500">Simpan Threshold</Button></div>
        </Card>
      )}
    </div>
  );
}

function Mini({ label, value, sub, tone, card }) {
  const inner = (
    <>
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`text-lg font-semibold ${tone || "text-white"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </>
  );
  return card ? <Card className="p-3 bg-slate-900 border-slate-800">{inner}</Card> : <div className="p-3 rounded bg-slate-800/40">{inner}</div>;
}
